import type { Deal, DevelopmentProfile, DevelopmentScenario } from "../types";
import { calcDevelopmentScenario } from "../lib/development";
import { money0, pct } from "../lib/format";
import { DevelopmentResearchRepository } from "./DevelopmentResearchRepository";
import { westDixieResearch } from "../data/westDixieResearch";
import { DevelopmentBudgetWorkbench } from "./DevelopmentBudgetWorkbench";
import { TaylorParkCommandCenter } from "./TaylorParkCommandCenter";

const NAV = [
  ["dev-command", "Command center"], ["dev-roadmap", "Roadmap"], ["dev-aerial", "Aerial"], ["dev-zoning", "Existing zoning"],
  ["dev-research", "Research"], ["dev-budget", "Cost budget"], ["dev-proforma", "Revenue"], ["dev-analytics", "Cash flow"],
  ["dev-readiness", "Readiness"], ["dev-risk", "Risks"], ["dev-report", "Report"],
];

const statusStyle = (status: "confirmed" | "verify" | "open") => ({
  background: status === "confirmed" ? "#e3f2e8" : status === "verify" ? "#f6edd8" : "#f6dfdc",
  color: status === "confirmed" ? "var(--pass)" : status === "verify" ? "var(--brass-deep)" : "var(--fail)",
});

export function DevelopmentFeasibility({
  deal,
  onChange,
  onDelete,
}: {
  deal: Deal;
  onChange: (patch: Partial<Deal>) => void;
  onDelete: () => void;
}) {
  const p = deal.development!;
  const updateProfile = (patch: Partial<DevelopmentProfile>) => onChange({ development: { ...p, ...patch } });
  const updateScenario = (index: number, key: keyof DevelopmentScenario, value: number) => {
    const scenarios = p.scenarios.map((s, i) => i === index ? { ...s, [key]: value } : s);
    updateProfile({ scenarios });
  };

  return (
    <>
      <nav className="subnav noprint">
        {NAV.map(([id, label]) => <a key={id} href={`#sec-${id}`}>{label}</a>)}
      </nav>
      <TaylorParkCommandCenter deal={deal} onChange={onChange} />
      <div className="wrap">
        <h2 className="sechead" id="sec-dev-overview">Development feasibility — concept stage</h2>
        <div className="grid g4">
          <Kpi label="Site" value={`${p.grossAcres.toFixed(2)} ac`} sub={`${p.lotSqFt.toLocaleString()} sf gross · net buildable unknown`} />
          <Kpi label="Control" value={`${p.leaseYears}-yr lease`} sub={`${money0(p.annualGroundRent)}/year proposed rent`} />
          <Kpi label="Base program" value={`${p.scenarios[0]?.units ?? 0} units`} sub={`${(p.scenarios[0]?.retailSf ?? 0).toLocaleString()} sf retail`} />
          <Kpi label="Current zoning" value="CF" sub="mixed-use is not by right" tone="warn" />
        </div>
        <div className="pe">
          <div className="l">◆ Current recommendation</div>
          <p>{p.recommendation}</p>
        </div>
        <div className="flag warn"><span>◐</span><div><b>Preliminary feasibility only.</b> The package stops before a financeable development pro forma. The model below turns its published program and cost ranges into an editable first pass. Broker rents, GC costs, phasing, financing, taxes, absorption, incentives, and the ground lease must be validated before an investment decision.</div></div>

        <h2 className="sechead" id="sec-dev-facts">Parcel and evidence record</h2>
        <div className="grid g2">
          <div className="card pad">
            <div className="eyebrow">Confirmed identity</div>
            <table className="t" style={{ marginTop: 8 }}><tbody>
              <Row label="Address" value={deal.addr} />
              <Row label="Folio" value={p.folio} />
              <Row label="Owner" value={p.owner} />
              <Row label="Site area" value={`${p.grossAcres.toFixed(2)} ac / ${p.lotSqFt.toLocaleString()} sf`} />
              <Row label="Existing improvements" value={`${p.existingBuildings} buildings / ~${p.existingBuildingSf.toLocaleString()} sf`} />
              <Row label="Assessed / PA market" value={`${money0(p.assessedValue)} / ${money0(p.marketValue)}`} />
              <Row label="Census tract" value={p.censusTract} />
              <Row label="Evidence date" value={p.asOf} />
            </tbody></table>
          </div>
          <div className="card pad">
            <div className="eyebrow">Planning posture</div>
            <table className="t" style={{ marginTop: 8 }}><tbody>
              <Row label="Current" value={p.currentZoning} />
              <Row label="Future land use" value="Recreation / Open Space (City GIS)" />
              <Row label="Target" value={p.targetZoning} />
              <Row label="Flood" value="FEMA Zone X / outside SFHA" />
              <Row label="Brownfield" value="OPEN - Taylor Park ERIC_9217" />
              <Row label="Opportunity Zone" value="Yes - tract 12086000206" />
              <Row label="Ground lease" value={`Proposed ${p.leaseYears} years at ${money0(p.annualGroundRent)}/year`} />
            </tbody></table>
          </div>
        </div>
        <div className="card pad" style={{ marginTop: 12 }}>
          <div className="eyebrow">What the package establishes</div>
          <ul style={{ margin: "10px 0 0", paddingLeft: 20, lineHeight: 1.65 }}>
            {p.facts.map((fact) => <li key={fact}>{fact}</li>)}
          </ul>
        </div>

        <DevelopmentResearchRepository research={p.research ?? westDixieResearch} onChange={(research) => updateProfile({ research })} />

        <DevelopmentBudgetWorkbench profile={p} onChange={(budget) => updateProfile({ budget })} />

        <h2 className="sechead" id="sec-dev-proforma">Revenue, NOI and residual value</h2>
        <div className="pe"><div className="l">◆ How to read it</div><p>Gold fields are working assumptions you can edit. Revenue is annual; retail rent is annual per square foot. Stabilized value is NOI divided by exit cap. Cost midpoint is the middle of the package's Level-5 range.</p></div>
        {p.scenarios.map((scenario, index) => (
          <ScenarioCard key={scenario.id} scenario={scenario} onNumber={(key, value) => updateScenario(index, key, value)} />
        ))}
        <div className="flag bad"><span>!</span><div><b>Initial conclusion:</b> under the package's conservative public-market rents and Level-5 construction ranges, both rental-only scenarios show a material value-to-cost gap. That is not a final “no”; it identifies the work: validate costs, add for-sale/phased revenue, quantify incentives/public contributions, refine parking, and solve density/entitlement before raising capital.</div></div>

        <h2 className="sechead" id="sec-dev-path">Entitlement and public-partner path</h2>
        <div className="grid g2">
          <div className="card pad">
            <div className="eyebrow">Critical path</div>
            <ol style={{ margin: "10px 0 0", paddingLeft: 20, lineHeight: 1.65 }}>
              {p.entitlementPath.map((item) => <li key={item}>{item}</li>)}
            </ol>
          </div>
          <div className="card pad">
            <div className="eyebrow">Potential feasibility offsets</div>
            <ul style={{ margin: "10px 0 0", paddingLeft: 20, lineHeight: 1.65 }}>
              {p.incentives.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </div>

        <h2 className="sechead" id="sec-dev-risk">Live risk and verification register</h2>
        <div className="card pad"><div className="tablewrap"><table className="t">
          <thead><tr><th>Item</th><th>Status</th><th>Current read</th></tr></thead>
          <tbody>{p.risks.map((risk) => <tr key={risk.label}>
            <td><b>{risk.label}</b></td>
            <td><span className="tag" style={statusStyle(risk.status)}>{risk.status.toUpperCase()}</span></td>
            <td style={{ color: "var(--muted)" }}>{risk.detail}</td>
          </tr>)}</tbody>
        </table></div></div>

        <h2 className="sechead" id="sec-dev-sources">Primary sources and working record</h2>
        <div className="card pad">
          <p style={{ marginTop: 0 }}>The complete source package remains the controlling working paper. These links reopen the main public records used to verify the in-app record.</p>
          <ul style={{ marginBottom: 0, paddingLeft: 20, lineHeight: 1.8 }}>
            {p.sourceLinks.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.label} ↗</a></li>)}
          </ul>
        </div>
        <button className="btn btn-g btn-sm" style={{ marginTop: 18 }} onClick={onDelete}>🗑 Delete this deal</button>
      </div>
    </>
  );
}

function ScenarioCard({ scenario: s, onNumber }: { scenario: DevelopmentScenario; onNumber: (key: keyof DevelopmentScenario, value: number) => void }) {
  const m = calcDevelopmentScenario(s);
  const fields: { key: keyof DevelopmentScenario; label: string; percent?: boolean; money?: boolean }[] = [
    { key: "units", label: "Units" }, { key: "oneBrPct", label: "1BR mix", percent: true },
    { key: "oneBrRent", label: "1BR rent/mo", money: true }, { key: "twoBrRent", label: "2BR rent/mo", money: true },
    { key: "retailSf", label: "Retail sf" }, { key: "retailRentPsf", label: "Retail $/sf/yr", money: true },
    { key: "otherIncomePerUnitMonthly", label: "Other $/unit/mo", money: true }, { key: "vacancyPct", label: "Vacancy", percent: true },
    { key: "expensePct", label: "OpEx / EGI", percent: true }, { key: "exitCap", label: "Exit cap", percent: true },
    { key: "parkingRatio", label: "Parking / unit" },
  ];
  const inputValue = (key: keyof DevelopmentScenario, percent?: boolean) => percent ? Number(s[key]) * 100 : Number(s[key]);
  return <div className="card pad" style={{ marginBottom: 14 }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
      <div><div className="fr" style={{ fontSize: 20, color: "var(--forest)", fontWeight: 600 }}>{s.name}</div><div className="note" style={{ maxWidth: 760 }}>{s.note}</div></div>
      <span className="tag" style={{ background: "var(--cream-200)", color: "var(--forest)" }}>WORKING ASSUMPTIONS</span>
    </div>
    <div className="inputs" style={{ marginTop: 14 }}>
      {fields.map((f) => <div className="fld" key={String(f.key)}><label>{f.label}</label><input type="number" step="any" style={{ borderColor: "var(--brass)" }} value={inputValue(f.key, f.percent)} onChange={(e) => onNumber(f.key, (Number(e.target.value) || 0) / (f.percent ? 100 : 1))} /></div>)}
    </div>
    <div className="grid g4" style={{ marginTop: 14 }}>
      <Kpi label="Annual GPR" value={money0(m.grossPotentialRevenue)} sub={`${m.oneBrUnits} 1BR + ${m.twoBrUnits} 2BR`} />
      <Kpi label="Stabilized NOI" value={money0(m.noi)} sub={`${pct(m.yieldOnCost)} yield on midpoint cost`} />
      <Kpi label="Stabilized value" value={money0(m.stabilizedValue)} sub={`${pct(s.exitCap)} exit cap`} />
      <Kpi label="Value / cost" value={pct(m.valueToCost)} sub={`${money0(m.valueCostGap)} vs midpoint`} tone={m.valueCostGap >= 0 ? "good" : "bad"} />
    </div>
    <div className="tablewrap" style={{ marginTop: 12 }}><table className="t"><tbody>
      <Row label="Residential rent" value={money0(m.residentialGross)} />
      <Row label="Retail + other income" value={money0(m.retailGross + m.otherIncome)} />
      <Row label={`Vacancy / credit loss (${pct(s.vacancyPct, 0)})`} value={`-${money0(m.grossPotentialRevenue - m.effectiveGrossIncome)}`} />
      <Row label="Effective gross income" value={money0(m.effectiveGrossIncome)} />
      <Row label={`Operating expenses (${pct(s.expensePct, 0)} of EGI)`} value={`-${money0(m.operatingExpenses)}`} />
      <Row label="Net operating income" value={money0(m.noi)} strong />
      <Row label="Level-5 development cost range" value={`${money0(s.costLow)} - ${money0(s.costHigh)}`} />
      <Row label="Cost midpoint / parking count" value={`${money0(m.costMid)} / ~${m.parkingSpaces.toLocaleString()} spaces`} strong />
    </tbody></table></div>
  </div>;
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return <tr className={strong ? "tot" : undefined}><td>{label}</td><td className="n">{value}</td></tr>;
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "good" | "warn" | "bad" }) {
  const color = tone === "good" ? "var(--pass)" : tone === "bad" ? "var(--fail)" : tone === "warn" ? "var(--brass-deep)" : undefined;
  return <div className="card kpi"><div className="kl">{label}</div><div className="kv" style={{ color }}>{value}</div><div className="ks">{sub}</div></div>;
}
